import React, { useState } from "react";
import { Compass, Copy, Check, AlertTriangle } from "lucide-react";
import iaimsHeader from "../../assets/IAIMS_header.png";

const IPhoneSafariRequiredOverlay: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    try {
      const url = typeof window !== "undefined" ? window.location.href : "https://eattendancewb.in";
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (e) {
      console.error("Clipboard write error:", e);
    }
  };

  return (
    <div className="min-h-[100dvh] w-full bg-gradient-to-b from-gray-900 via-gray-950 to-black text-white font-sans flex flex-col justify-between p-4 sm:p-6 selection:bg-[#FF6B1A] selection:text-white">
      {/* Top Header Logo */}
      <div className="pt-[max(0.75rem,env(safe-area-inset-top))] flex items-center justify-center shrink-0">
        <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/10 flex items-center justify-center">
          <img
            src={iaimsHeader}
            alt="IAIMS Logo"
            className="h-8 w-auto object-contain"
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="w-full max-w-sm mx-auto bg-gray-900/90 border border-gray-800 rounded-3xl p-6 shadow-2xl backdrop-blur-xl text-center space-y-5 my-auto">
        {/* Safari Compass Icon */}
        <div className="relative inline-flex items-center justify-center">
          <div className="absolute inset-0 bg-[#FF6B1A]/20 rounded-full blur-xl animate-pulse" />
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center shadow-lg border border-blue-400/30 shrink-0">
            <Compass className="w-10 h-10 animate-spin-slow" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-amber-500 text-gray-950 flex items-center justify-center border-2 border-gray-900 shadow-md">
            <AlertTriangle className="w-4 h-4" />
          </div>
        </div>

        {/* Title & Subtitle */}
        <div className="space-y-1.5">
          <h1 className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
            Safari Browser Required
          </h1>
          <h2 className="text-xs font-semibold text-[#FF6B1A] uppercase tracking-wider">
            সাফারি ব্রাউজারে খুলুন
          </h2>
        </div>

        <p className="text-xs text-gray-300 leading-relaxed font-medium px-1">
          Camera, location, and facial attendance features on iPhone require the native **Safari** browser. Please switch to Safari to continue.
        </p>

        {/* Instructions Steps */}
        <div className="bg-gray-950/80 rounded-2xl p-4 border border-gray-800/80 text-left space-y-2.5 text-xs text-gray-300">
          <div className="flex items-start space-x-2.5">
            <span className="w-5 h-5 rounded-full bg-[#FF6B1A]/20 text-[#FF6B1A] font-bold text-[11px] flex items-center justify-center shrink-0 mt-0.5">
              1
            </span>
            <p className="flex-1">
              Tap the <strong className="text-white">Copy Link</strong> button below.
            </p>
          </div>

          <div className="flex items-start space-x-2.5">
            <span className="w-5 h-5 rounded-full bg-[#FF6B1A]/20 text-[#FF6B1A] font-bold text-[11px] flex items-center justify-center shrink-0 mt-0.5">
              2
            </span>
            <p className="flex-1">
              Open the native <strong className="text-white">Safari App</strong> on your iPhone.
            </p>
          </div>

          <div className="flex items-start space-x-2.5">
            <span className="w-5 h-5 rounded-full bg-[#FF6B1A]/20 text-[#FF6B1A] font-bold text-[11px] flex items-center justify-center shrink-0 mt-0.5">
              3
            </span>
            <p className="flex-1">
              Paste the link into the address bar & hit <strong className="text-white">Go</strong>.
            </p>
          </div>
        </div>

        {/* Action Button */}
        <div className="pt-1">
          <button
            onClick={handleCopyLink}
            className={`w-full py-3.5 px-4 rounded-2xl text-xs font-bold flex items-center justify-center space-x-2 shadow-lg transition-all active:scale-95 ${
              copied
                ? "bg-emerald-600 text-white"
                : "bg-gradient-to-r from-[#FF6B1A] to-[#EA580C] text-white hover:brightness-110"
            }`}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                <span>Link Copied! Open Safari to Paste</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy Link / লিংক কপি করুন</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Footer Info */}
      <div className="pb-[max(0.5rem,env(safe-area-inset-bottom))] text-center shrink-0">
        <p className="text-[10px] text-gray-500 font-mono">
          IAIMS Integrated Attendance & Identity System • iOS Safari Only
        </p>
      </div>
    </div>
  );
};

export default IPhoneSafariRequiredOverlay;
